import { useCallback, useEffect, useState, useRef } from "react";
import { ChatEvent, FlowEvent } from "./useGptFlow";
import { useGptGateway } from "./useGptGateway";
import {
  ChatMessage,
  SetupFlowResponse,
  useGpt,
  Message,
  ToolCall,
  ToolCallResponse,
  NodeStatusUpdateEvent,
} from "@/shared/contexts/gptgateway.context";
import { useApplicationEvents } from "@/shared/utils/hooks/useApplicationEvents";
import { EVENT_NAMES, trackAppEvent } from "@/shared/utils/events/trackEvent";

// Type guards
const isMessage = (item: any): item is Message => item?.type === "message";
const isToolCall = (item: any): item is ToolCall =>
  item?.type === "function_call";
const isToolCallResponse = (item: any): item is ToolCallResponse =>
  item?.type === "function_call_output";

interface UseGptGatewayChatProps {
  flowId: string;
  scope: { [key: string]: any };
  isReady: boolean;
  focusedContext: {
    id: string;
    prompt?: string;
    params?: Record<string, any>;
    message?: ChatMessage;
  };
}

interface GptPromptEventData {
  prompt: string;
  params?: Record<string, any>;
  message?: ChatMessage; // Optional: add the message to the messages array to display it in the chat
}

interface StatusUpdateEventContent {
  [key: string]: any;
}

/*
  This is a helper function to emit a custom event to the window
  It is used to send the user prompt to the useGptFlow hook independently of the GatewayChat component
*/
export const emitGptPromptEvent =
  (flowId: string, focusedContextId: string) => (data: GptPromptEventData) => {
    const { prompt, params, message } = data;

    const flowName = `gpt-prompt-${flowId}`;
    const event = new CustomEvent(`${flowName}-${focusedContextId}`, {
      detail: {
        prompt,
        params,
        message,
      },
    });

    window.dispatchEvent(event);

    trackAppEvent(EVENT_NAMES.SEND_GPT_PROMPT, {
      flowId,
    });
  };

/**
 * This hook is used to connect the GatewayChat component to the useGptFlow hook
 * It is used to send the user prompt to the useGptFlow hook independently of the GatewayChat component
 * It is also used to handle the chat events from the useGptFlow hook
 * @param flowId - The id of the flow to connect to
 * @param scope - The scope of the flow to connect to
 * @param isReady - Whether the flow is ready to be used. Defaults to false. If true, the flow will be initialized and the messages will be set.
 * @param focusedContext - The context that is currently focused on. Defaults to { id: "default"}. Used for separating conversations
 * @returns The flow status, the messages and the stream
 *
 * Do not use this hook directly, use the GatewayChat component instead
 * If direct usage of gateway is needed, use the useGptFlow hook instead
 */

export const useGptGatewayChat = (
  { flowId, scope, focusedContext, isReady }: UseGptGatewayChatProps = {
    flowId: "",
    isReady: false,
    scope: {},
    focusedContext: { id: "default" },
  }
) => {
  const [messages, setMessages] = useState<Record<string, ChatMessage[]>>({
    default: [],
  });

  const [stream, setStream] = useState<string[]>([]);

  const [activeConversations, setActiveConversations] = useState<
    Record<string, SetupFlowResponse>
  >({});

  const [tokens, setTokens] = useState<Record<string, string>>({});

  const { fetchToken } = useGptGateway();

  const { registerEventHandler, emitApplicationEvent } = useApplicationEvents();

  const eventHandler = useCallback(
    (evt: NodeStatusUpdateEvent<StatusUpdateEventContent> | ChatEvent) => {
      // Check out FlowEvent defined in the useGptFlow.ts hook file
      // Here only chat events are relevant (which in turn contain an LLMEvent)
      // If you want to process other types of events too, maybe it would make sense to
      // put the useGptFlow hook outside of the GatewayChat component?

      if (evt.type === "nodeStatusUpdate") {
        if (evt?.content.type === "evaluatingRow") {
          emitApplicationEvent("CanvasSetFocusedEditor", {
            sectionId: `assessment_row_${(evt as any).content?.rowId}`,
          });

          const element = document.getElementById(
            `assessment_row_${(evt as any).content?.rowId}`
          );
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }

        if (evt?.content.type === "evaluationComplete") {
          const text = (evt as any).content?.assessmentSection?.content;
          if (text) {
            emitApplicationEvent("CanvasEditorChange", {
              sectionId: `assessment_row_${(evt as any).content?.rowId}`,
              text,
            });
          }
        }
      }

      if (evt.type === "chatEvent") {
        const chatEvt = (evt as ChatEvent).chatEvent;
        // console.log("[useGptGatewayChat] Chat event:", chatEvt.event, chatEvt);

        if (chatEvt.event == "on_llm_start") {
          setStream([]);
        } else if (chatEvt.event == "output_delta") {
          // console.log("[useGptGatewayChat] Output delta:", chatEvt.delta);
          setStream((buf) => [...buf, chatEvt.delta!]);
        } else if (chatEvt.event == "new_output_block") {
          // Start of a new incomplete output block for streaming
          // console.log("[useGptGatewayChat] New output block");
          setStream([]);
        } else if (chatEvt.event == "tool_call") {
          // Handle tool call event
          if (chatEvt.item && isToolCall(chatEvt.item)) {
            // console.log("[useGptGatewayChat] Tool call:", chatEvt.item);
            setMessages((msgs) => ({
              ...msgs,
              [focusedContext.id]: [
                ...(msgs[focusedContext.id] || []),
                {
                  role: "tool_call" as const,
                  content: chatEvt.item as ToolCall,
                },
              ],
            }));
          }
        } else if (chatEvt.event == "tool_call_result") {
          // Handle tool call result
          if (chatEvt.item && isToolCallResponse(chatEvt.item)) {
            // console.log("[useGptGatewayChat] Tool call result:", chatEvt.item);
            setMessages((msgs) => ({
              ...msgs,
              [focusedContext.id]: [
                ...(msgs[focusedContext.id] || []),
                {
                  role: "tool_call_response" as const,
                  content: chatEvt.item as ToolCallResponse,
                },
              ],
            }));
          }
        } else if (chatEvt.event == "full_output_block") {
          // Complete output block (streaming or non-streaming)
          if (chatEvt.item && isMessage(chatEvt.item)) {
            const content = chatEvt.item.content;
            // console.log("[useGptGatewayChat] Full output block:", content);
            setStream([]);
            setMessages((msgs) => ({
              ...msgs,
              [focusedContext.id]: [
                ...(msgs[focusedContext.id] || []),
                {
                  role: "assistant" as const,
                  content,
                },
              ],
            }));
          }
        } else if (chatEvt.event == "on_llm_end") {
          // End of LLM generation - clear stream
          // console.log("[useGptGatewayChat] LLM end");
          setStream([]);
        } else {
          // Do nothing
        }
      }
    },
    [flowId, setMessages, focusedContext.id]
  );

  const { getFlowStatus, initializeFlow, sendUserPrompt, socket } = useGpt();

  const conversationId = activeConversations[focusedContext.id]?.conversationId;
  const flowStatus = getFlowStatus(conversationId);

  // This cannot have changing dependencies as it will be used as a listener callback which is not re-created on every render
  const promptEventHandler = useCallback(
    (conversationId: string, contextId: string) => (data: any) => {
      const { prompt, params, message } = data;
      sendUserPrompt({ prompt, params, conversationId });
      if (message) {
        setMessages((msgs) => ({
          ...msgs,
          [contextId]: [...msgs[contextId], message],
        }));
      }
    },
    [sendUserPrompt]
  );

  const initializeNewFlow = async () => {
    // Check if we already have a token for this context
    let token: string | null | undefined = tokens[focusedContext.id];

    // If not, fetch a new token with the scope
    if (!token && isReady) {
      const fetchedToken = await fetchToken(scope as any);
      if (fetchedToken) {
        token = fetchedToken;
        setTokens((prev) => ({
          ...prev,
          [focusedContext.id]: fetchedToken,
        }));
      }
    }

    if (token && socket) {
      const res = await initializeFlow(flowId, token as any);
      if (res) {
        setActiveConversations((prev) => ({
          ...prev,
          [focusedContext.id]: res,
        }));
        socket?.on(res.conversationId, eventHandler);

        const eventName = `gpt-prompt-${flowId}-${focusedContext.id}`;

        registerEventHandler(
          eventName,
          promptEventHandler(res.conversationId, focusedContext.id)
        );

        if (focusedContext.prompt) {
          console.log("focusedContext.prompt", focusedContext);
          sendUserPrompt({
            prompt: focusedContext.prompt,
            params: focusedContext.params,
            conversationId: res.conversationId,
          });
        }

        if (focusedContext.message) {
          setMessages((prev) => ({
            ...prev,
            [focusedContext.id]: [focusedContext.message!],
          }));
        }
      }
    }
  };

  useEffect(() => {
    if (
      focusedContext.id &&
      !activeConversations[focusedContext.id] &&
      socket &&
      isReady // Wait for socket to be ready and flow to be ready
    ) {
      initializeNewFlow();
    }
  }, [focusedContext.id, socket, activeConversations, isReady]);

  const startNewConversation = async (newScope?: { [key: string]: any }) => {
    // Clear the token for the current context to force a new token fetch
    setTokens((prev) => {
      const updated = { ...prev };
      delete updated[focusedContext.id];
      return updated;
    });

    // Clear the active conversation
    setActiveConversations((prev) => {
      const updated = { ...prev };
      delete updated[focusedContext.id];
      return updated;
    });

    // If a new scope is provided, fetch token and initialize
    if (newScope && isReady) {
      const token = await fetchToken(newScope as any);
      if (token) {
        setTokens((prev) => ({
          ...prev,
          [focusedContext.id]: token,
        }));
      }
    }
  };

  return {
    flowStatus,
    messages,
    stream,
    startNewConversation,
  };
};
